// Drag and drop on the Budget tab. Long-press a grip (touch) or press and move it (mouse) to pick
// up a category or a group, drag to a place shown by a drop line, and let go. Categories move
// within their group or into another group of the same kind; a group of the other kind shows a
// refused line and the drop snaps back. Groups reorder among themselves. The page does not scroll
// while dragging (the grip has touch-action: none and touchmove is cancelled); near the top or
// bottom edge the page scrolls itself. Saving goes through main.ts (the move routes).

import { canDrop, nearestSlot, targetIndex, type Slot } from './drag-math';

export interface DragHandlers {
  category(id: string, groupId: string, index: number): void;
  group(id: string, index: number): void;
  /** Dropped on a group of the other kind: it snapped back. */
  refused(): void;
}

const LONG_PRESS_MS = 300;
const EDGE = 72;

/** Wire drag and drop once; it works on whatever the Budget tab has rendered when a drag starts. */
export function initDrag(h: DragHandlers): void {
  let pending: { grip: HTMLElement; x: number; y: number; id: number; timer?: ReturnType<typeof setTimeout>; mouse: boolean } | null = null;
  let drag: {
    what: 'category' | 'group';
    id: string;
    row: HTMLElement;
    ghost: HTMLElement;
    line: HTMLElement;
    fromGroup: string;
    fromIndex: number;
    kind: string;
    offset: number;
    clientY: number;
    slots: Slot[];
    kinds: Map<string, string>;
    target: Slot | null;
    frame: number;
  } | null = null;

  const pageTop = (el: Element) => el.getBoundingClientRect().top + window.scrollY;
  const pageBottom = (el: Element) => el.getBoundingClientRect().bottom + window.scrollY;

  function slotsFor(what: 'category' | 'group'): { slots: Slot[]; kinds: Map<string, string> } {
    const slots: Slot[] = [];
    const kinds = new Map<string, string>();
    const blocks = [...document.querySelectorAll<HTMLElement>('#app .gblock[data-group]')];
    if (what === 'group') {
      blocks.forEach((b, i) => {
        const r = b.getBoundingClientRect();
        slots.push({ groupId: '', slot: i, y: pageTop(b) - 6, left: r.left, width: r.width });
        if (i === blocks.length - 1) slots.push({ groupId: '', slot: i + 1, y: pageBottom(b) + 6, left: r.left, width: r.width });
      });
      return { slots, kinds };
    }
    for (const b of blocks) {
      const groupId = b.dataset.group!;
      kinds.set(groupId, b.dataset.kind ?? '');
      const rows = [...b.querySelectorAll<HTMLElement>(':scope .cats > .cat[data-cat]')];
      const r = b.getBoundingClientRect();
      if (!rows.length) {
        // Folded or empty: drop at its end, under the header.
        const head = b.querySelector('.gsub') ?? b;
        slots.push({ groupId, slot: b.classList.contains('folded') ? Number(b.dataset.count ?? 0) : 0, y: pageBottom(head), left: r.left, width: r.width });
        continue;
      }
      rows.forEach((row, i) => slots.push({ groupId, slot: i, y: pageTop(row), left: r.left, width: r.width }));
      slots.push({ groupId, slot: rows.length, y: pageBottom(rows[rows.length - 1]), left: r.left, width: r.width });
    }
    return { slots, kinds };
  }

  function start() {
    if (!pending) return;
    const grip = pending.grip;
    const what = grip.dataset.grip as 'category' | 'group';
    const id = grip.dataset.id!;
    const row = (what === 'group' ? grip.closest<HTMLElement>('.gblock') : grip.closest<HTMLElement>('.cat'))!;
    if (!row) return;
    const block = row.closest<HTMLElement>('.gblock')!;
    const siblings = what === 'group' ? [...document.querySelectorAll('#app .gblock[data-group]')] : [...block.querySelectorAll(':scope .cats > .cat[data-cat]')];
    const rect = (what === 'group' ? row.querySelector('.ghead')! : row).getBoundingClientRect();
    const ghost = (what === 'group' ? row.querySelector<HTMLElement>('.ghead')! : row).cloneNode(true) as HTMLElement;
    ghost.classList.add('ghost');
    ghost.style.cssText = `position:fixed;left:${rect.left}px;top:${rect.top}px;width:${rect.width}px;z-index:30;pointer-events:none`;
    document.body.appendChild(ghost);
    const line = document.createElement('div');
    line.className = 'dropline';
    document.body.appendChild(line);
    row.classList.add('dragging');
    document.documentElement.classList.add('is-dragging');
    const { slots, kinds } = slotsFor(what);
    drag = {
      what,
      id,
      row,
      ghost,
      line,
      fromGroup: what === 'group' ? '' : block.dataset.group!,
      fromIndex: siblings.indexOf(row),
      kind: block.dataset.kind ?? '',
      offset: pending.y - rect.top,
      clientY: pending.y,
      slots,
      kinds,
      target: null,
      frame: 0,
    };
    pending = null;
    navigator.vibrate?.(10);
    update();
    drag.frame = requestAnimationFrame(tick);
  }

  function update() {
    if (!drag) return;
    drag.ghost.style.top = `${drag.clientY - drag.offset}px`;
    const s = nearestSlot(drag.slots, drag.clientY + window.scrollY);
    drag.target = s;
    if (!s) return;
    const ok = drag.what === 'group' || canDrop(drag.kind, drag.kinds.get(s.groupId) ?? '');
    drag.line.classList.toggle('no', !ok);
    drag.line.style.cssText = `top:${s.y - window.scrollY - 1}px;left:${s.left}px;width:${s.width}px`;
  }

  // Near the top or bottom edge, scroll the page (the slots are in page coordinates).
  function tick() {
    if (!drag) return;
    const y = drag.clientY;
    const bottom = window.innerHeight - EDGE - 40; // above the tab bar
    const step = y < EDGE + 40 ? -Math.ceil((EDGE + 40 - y) / 6) : y > bottom ? Math.ceil((y - bottom) / 6) : 0;
    if (step) {
      window.scrollBy(0, step);
      update();
    }
    drag.frame = requestAnimationFrame(tick);
  }

  function finish(drop: boolean) {
    if (!drag) return;
    const d = drag;
    drag = null;
    cancelAnimationFrame(d.frame);
    d.line.remove();
    document.documentElement.classList.remove('is-dragging');
    const target = d.target;
    const ok = !!target && (d.what === 'group' || canDrop(d.kind, d.kinds.get(target.groupId) ?? ''));
    const settle = () => {
      d.ghost.remove();
      d.row.classList.remove('dragging');
    };
    if (!drop || !target || !ok) {
      // Snap back: the ghost returns to the row's place.
      const r = (d.what === 'group' ? d.row.querySelector('.ghead')! : d.row).getBoundingClientRect();
      d.ghost.style.transition = 'top .18s ease';
      d.ghost.style.top = `${r.top}px`;
      setTimeout(settle, 190);
      if (drop && target && !ok) h.refused();
      return;
    }
    settle();
    const index = targetIndex(d.fromGroup, d.fromIndex, target);
    if (index === null) return;
    if (d.what === 'group') h.group(d.id, index);
    else h.category(d.id, target.groupId, index);
  }

  document.addEventListener('pointerdown', (e) => {
    const grip = (e.target as HTMLElement).closest<HTMLElement>('[data-grip]');
    if (!grip || drag || (e.pointerType === 'mouse' && e.button !== 0)) return;
    pending = { grip, x: e.clientX, y: e.clientY, id: e.pointerId, mouse: e.pointerType === 'mouse' };
    try {
      grip.setPointerCapture(e.pointerId);
    } catch {
      /* not capturable: moves still arrive on the document */
    }
    if (!pending.mouse) pending.timer = setTimeout(start, LONG_PRESS_MS);
  });
  document.addEventListener('pointermove', (e) => {
    if (drag) {
      drag.clientY = e.clientY;
      update();
      return;
    }
    if (!pending || e.pointerId !== pending.id) return;
    const moved = Math.hypot(e.clientX - pending.x, e.clientY - pending.y);
    if (pending.mouse && moved > 4) start();
    else if (!pending.mouse && moved > 12) {
      clearTimeout(pending.timer); // a swipe, not a long press
      pending = null;
    }
  });
  const end = (drop: boolean) => () => {
    if (pending) clearTimeout(pending.timer);
    pending = null;
    finish(drop);
  };
  document.addEventListener('pointerup', end(true));
  document.addEventListener('pointercancel', end(false));
  // iOS Safari: never scroll the page while a grip is pressed or a row is being dragged.
  document.addEventListener(
    'touchmove',
    (e) => {
      if (drag || pending) e.preventDefault();
    },
    { passive: false },
  );
  // A long press on the grip must not open the context menu or select text.
  document.addEventListener('contextmenu', (e) => {
    if ((e.target as HTMLElement).closest?.('[data-grip]')) e.preventDefault();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drag) finish(false);
  });
}
